import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, MessagesSquare, PlugZap, ShieldCheck, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CoWorkerPillar } from '../components/alt/CoWorkerPillar';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { BrandLogo } from '../components/alt/AppChrome';
import { SectionIntro } from '../components/alt/SectionIntro';

// Order carries the argument: connect the tools, let what they know compound,
// take that intelligence to where people already work, and build on it.
const SECTIONS = [{
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'workspace',
  label: 'Workspace',
  icon: MessagesSquare
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('connected-apps');
  const [audienceIndex, setAudienceIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const audiencePhrases = ['companies', 'founders', 'operators', 'teams'];
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  // An IntersectionObserver band was mis-firing here: these sections are much
  // taller than the band, so with direction-dependent tie-breaking the active
  // tab could jump a section early or lag a section behind. A single reference
  // line just below the sticky nav is unambiguous — the active section is
  // simply the last one whose top has passed it.
  useEffect(() => {
    const update = () => {
      const line = (navRef.current?.getBoundingClientRect().bottom ?? 140) + 24;
      let current: SectionId = SECTIONS[0].id;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= line) current = section.id;
      }
      setActiveSection(current);
    };
    update();
    window.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    // The pillar bar is the ONLY thing pinned to the top of the viewport on
    // this page — the site header scrolls away — so the section only needs to
    // clear the bar's own height, plus a little breathing room.
    // Align the section's FIRST piece of content (its eyebrow) rather than the
    // section box — the box carries 80–112px of its own top padding, which
    // otherwise reads as dead space under the bar. Falls back to the box.
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const anchor = el.querySelector('p, h2, h3') ?? el;
    const top = anchor.getBoundingClientRect().top + window.scrollY - navHeight - 18;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="giga-home relative min-h-screen bg-[#FAF8F4] pt-24 text-[#2B211A]" data-id="element-6196" data-mp="2zke46kysdra1">
      {/* The halftone is a texture, not a pattern to stare at: it sits behind
       the hero only and fades out radially, so the headline reads off clean
       paper while the page still has a surface. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[720px]" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
      backgroundSize: '8px 8px',
      WebkitMaskImage: 'radial-gradient(115% 90% at 50% 8%, #000 8%, rgba(0,0,0,0.55) 45%, transparent 82%)',
      maskImage: 'radial-gradient(115% 90% at 50% 8%, #000 8%, rgba(0,0,0,0.55) 45%, transparent 82%)'
    }} data-id="element-6197" data-mp="3dk71iz4tj5qz" />
      <section className="relative pb-10 pt-12 md:pb-14 md:pt-20" data-id="element-6198" data-mp="225jqeegemx8y">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6199" data-mp="223p4awtecz3a">
          <div className="mx-auto max-w-5xl text-center" data-id="element-6200" data-mp="1ul974k2mci3r">
            <motion.h1 initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            ease: [0.23, 1, 0.32, 1]
          }} className="font-title text-5xl font-bold leading-[0.95] tracking-[-0.028em] text-[#2B211A] [text-wrap:balance] md:text-7xl" data-id="element-6201" data-mp="1pnpgslsncevg">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-6202" data-mp="16wg4y5vele0p">
                <AnimatePresence mode="wait" initial={false} data-id="element-6203" data-mp="11xv547k2cwcs">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-6204" data-mp="11yeprw13lyui">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-6205" data-mp="9c6jp5tooidx" />
              that want to <HeadlineIcon icon={Zap} data-id="element-6206" data-mp="9dhnv6r1js5k" />
              <UnderscoredPhrase data-id="element-6207" data-mp="x0ppnep9gj2u">Go Faster</UnderscoredPhrase>
              <br data-id="element-6208" data-mp="x3bydhnw14mn" />
              and <HeadlineIcon icon={ShieldCheck} data-id="element-6209" data-mp="11zstwwxf2l9r" />
              <UnderscoredPhrase data-id="element-6210" data-mp="11zj1l2owg20w">Stay in Control</UnderscoredPhrase>
            </motion.h1>
          </div>
        </div>
      </section>

      {/* One contained segmented control rather than four separately bordered,
       separately shadowed floating pills. A single recessed track with one
       raised marker reads as a considered control; the previous treatment
       stacked four shadows and a saturated orange fill at 18px, which is what
       made it feel loud and cheap. */}
      <div ref={navRef} className="sticky top-0 z-40 border-b border-[#E7E0D4]/70 bg-[#FAF8F4]/80 backdrop-blur-xl" data-id="element-6211" data-mp="35fo3w258ljyp">
        <nav className="mx-auto flex max-w-[1280px] justify-center px-3 py-2.5 sm:px-6 md:px-10" aria-label="Section navigation" data-id="element-6212" data-mp="30hmburpu2ab3">
          <div className="hide-scrollbar flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-[#E7E0D4] bg-[#FBF8F3] p-1 shadow-[inset_0_1px_2px_rgba(43,33,26,0.05)]" data-id="element-6213" data-mp="3a7pap8jmyihc">
            {SECTIONS.map(section => {
            const isActive = activeSection === section.id;
            const Icon = section.icon;
            return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`relative inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-[13px] font-semibold transition-[color,background-color,box-shadow] duration-200 ease-out sm:px-4 sm:text-[14.5px] ${isActive ? 'bg-[#2B211A] text-white shadow-[0_2px_6px_rgba(43,33,26,0.22)]' : 'text-[#6B5D52] hover:bg-white hover:text-[#2B211A] hover:shadow-[0_1px_2px_rgba(43,33,26,0.06)]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-6214" data-mp="2vjuhlr1puc3q">
                  <Icon size={16} strokeWidth={1.9} className={`shrink-0 transition-colors duration-200 ${isActive ? 'text-[#F5A26B]' : 'text-[#A99A8C]'}`} data-id="element-6215" data-mp="2592bg8ps3vl3" />
                  <span className="whitespace-nowrap leading-none" data-id="element-6216" data-mp="2eyu8d9lcp0sq">{section.label}</span>
                </button>;
          })}
          </div>
        </nav>
      </div>

      <main data-id="element-6217" data-mp="2nza6s4n8lhqq">
        <section id="connected-apps" className="scroll-mt-24 border-b border-[#E7E0D4] bg-[#FAF8F4] py-24 md:py-32" data-id="element-6218" data-mp="2nz0hykdxhgmm">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6219" data-mp="2sy3lu1i69gt0">
            <ConnectedAppsPillar data-id="element-6220" data-mp="2xxg5kddqy7rd" />
            <div className="mt-20 md:mt-28" data-id="element-6221" data-mp="2xwevab4k1pap">
              <ConnectedAppsTable data-id="element-6222" data-mp="2xvdgpwobyko8" />
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-24 border-b border-[#E7E0D4] bg-white py-24 md:py-32" data-id="element-6223" data-mp="ym6nrpp85coz">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6224" data-mp="13lje4kfb11wb">
            <BrainFlowExperiment data-id="element-6225" data-mp="3gui1gf3h3hgb" />
          </div>
        </section>

        <section id="co-worker" className="scroll-mt-24 border-b border-[#E7E0D4] bg-[#FAF8F4] py-24 md:py-32" data-id="element-6226" data-mp="3c2ggm4s8oieo">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6227" data-mp="380lfb0ab650w">
            {/* Shared with the home page so the two cannot drift apart. */}
            <CoWorkerPillar data-id="element-6228" data-mp="330797ns9p55i" />
          </div>
        </section>
 
        <section id="workspace" className="scroll-mt-24 bg-white py-24 md:py-32" data-id="element-6229" data-mp="3m58x7q3riwk">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6230" data-mp="3vqpl9eayygv2">
            <PlatformPillar data-id="element-6231" data-mp="3vp4nlftcyhsz" />
          </div>
        </section>

        {/* White canvas: the diagram's group panels are cream, so they need a
         lighter ground now that the enclosing card is gone. */}
        <section className="border-t border-[#E7E0D4] bg-white py-24 md:py-32" data-id="element-6232" data-mp="2fcpp8ef8vbd3">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6233" data-mp="2famrf87uodxt">
            <ArchitectureWidget data-id="element-6234" data-mp="2acuyqbzdsnsk" />
          </div>
        </section>

        <section className="border-t border-[#E7E0D4] bg-[#FAF8F4] py-24 md:py-32" data-id="element-6235" data-mp="20u5hnozfgki0">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6236" data-mp="1vvlslfpjeslf">
            <AccelerationControlSection data-id="element-6237" data-mp="1vxf35639twyp" />
            <ClosingCTA data-id="element-6238" data-mp="2ak8cluq408iz" />
          </div>
        </section>
      </main>
    </div>;
}
// Sized in em so the tile tracks the headline as it scales across breakpoints.
function HeadlineIcon({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  // Bare mark, no tile: at headline scale the filled square read as a button
  // and boxed the icon away from the words it belongs to.
  return <span aria-hidden="true" className="mr-[0.14em] inline-flex size-[0.78em] -translate-y-[0.04em] items-center justify-center align-middle" data-id="element-6239" data-mp="2eog3nyk1i0kd">
      <Icon className="size-full text-[#EA580C]" strokeWidth={2.4} data-id="element-6240" data-mp="2olaxi45dbcsb" />
    </span>;
}
function UnderscoredPhrase({
  children
}: {
  children: React.ReactNode;
}) {
  // The rule draws itself left-to-right on load — one small piece of craft that
  // makes the headline feel authored rather than typeset.
  return <span className="relative inline-block align-baseline" data-id="element-6241" data-mp="35uwphk24oukv">
      <motion.span initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 0.62,
      delay: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="absolute inset-x-0 bottom-[-4px] h-[9px] origin-left rounded-[2px] bg-[#EA580C]" aria-hidden="true" data-id="element-6242" data-mp="3asf1xkfctrxm" />
      <span className="relative z-10" data-id="element-6243" data-mp="2n33wvqqz1bdo">{children}</span>
    </span>;
}
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
function AccelerationControlSection() {
  return <div data-id="element-6244" data-mp="ixua9mowbb6g">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-6245" data-mp="1gcg9lwviaru8" data-mpi={groupIndex}>
          <SectionIntro align="left" data-id="element-6246" data-mp="1blq6kqajz12p">
            <h2 className="font-title text-4xl font-bold leading-tight tracking-[-0.025em] text-[#2B211A] md:text-6xl" data-id="element-6247" data-mp="1bkouqjlpqajx">
              {group.heading}
            </h2>
          </SectionIntro>
          <div className="mt-8 border-y border-[#E7E0D4]" data-id="element-6248" data-mp="1nv7gibqrbg2x">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-[#E7E0D4] py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-6249" data-mp="1suiwhijauhjg" data-mpi={index}>
                <h3 className="font-title text-2xl font-bold leading-tight tracking-[-0.02em] text-[#2B211A] md:text-3xl" data-id="element-6250" data-mp="2gjleirhuiceh">{item.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-6251" data-mp="2lhmwta7g67ow">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
function ClosingCTA() {
  // The closing panel is the one dark surface on the page, so it carries the
  // brand halftone rather than reading as a flat black rectangle.
  return <div className="relative mt-20 overflow-hidden rounded-[28px] bg-[#2B211A] px-8 py-14 text-center shadow-[0_40px_100px_-45px_rgba(43,33,26,0.7)] md:mt-28 md:px-16 md:py-20" data-id="element-6252" data-mp="tl9g600dl74">
      <div aria-hidden="true" className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" data-id="element-6253" data-mp="34atoyj79k8yo" />
      <div className="relative" data-id="element-6254" data-mp="2zd205y1x12yp">
        <h2 className="mx-auto max-w-[24ch] font-title text-4xl font-bold leading-[1.05] tracking-[-0.025em] text-white md:text-[52px]" data-id="element-6255" data-mp="2zdtkudtz5ga7">
          One platform to run your company's AI on.
        </h2>
        <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-white/65" data-id="element-6256" data-mp="3994zyx9aopih">
          Connect your tools, build your workflows, put a co worker where your
          team already is, and let the brain get sharper every week.
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-6257" data-mp="w4026odnyub8">
          <a href="https://app.getgiga.com/signup" className="inline-flex items-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-base font-bold text-white shadow-[0_14px_30px_-12px_rgba(234,88,12,0.85)] transition-[background-color,transform,box-shadow] duration-200 ease-out hover:-translate-y-px hover:bg-[#C2410C]" data-id="element-6258" data-mp="112jnqnoci432">
            Get started for free
            <ArrowRight size={17} data-id="element-6259" data-mp="1euzthw8msg7o" />
          </a>
          <Link to="/demo/book" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-base font-bold text-white transition-[border-color,background-color] duration-200 ease-out hover:border-white/60 hover:bg-white/5" data-id="element-6260" data-mp="1jtubivyry0qq">
            Book a demo
          </Link>
        </div>
        <p className="mt-6 text-[13.5px] text-white/45" data-id="element-6261" data-mp="1xstze1egqblu">
          $100 in credits included · no card needed
        </p>
      </div>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  anthropic: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  anthropic: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  anthropic: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  anthropic: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  anthropic: null,
  giga: 'Coming soon'
}];
export function ConnectedAppsTable() {
  return <div className="mx-auto max-w-[900px] overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.04),0_30px_80px_-48px_rgba(43,33,26,0.4)] md:overflow-visible" data-id="element-6262" data-mp="1i9hmkbyijg0y">
      {/* Horizontal scroll is only needed on narrow screens, and it clips the
       hover panel — so above md the table simply lays out at full width. */}
      <div className="overflow-x-auto md:overflow-visible" data-id="element-6263" data-mp="2110pkisjeu2z">
        <table className="w-full min-w-[660px] border-collapse text-left" data-id="element-6264" data-mp="213wjzx56f0mo">
          <thead data-id="element-6265" data-mp="2f1v0b832574t">
            <tr className="border-b border-[#E7E0D4]" data-id="element-6266" data-mp="2ez8nasxedzf7">
              <th scope="col" className="w-[48%] px-5 py-4 md:px-6" data-id="element-6267" data-mp="2b0rgkahw2qyb">
                <span className="sr-only" data-id="element-6268" data-mp="2toa5ja58eqzp">Feature</span>
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 text-center align-bottom" data-id="element-6269" data-mp="2tm7f4p062vug">
                <ColumnHead slug="chatgpt" name="ChatGPT" data-id="element-6270" data-mp="2ymbv8dx5xok5" />
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 text-center align-bottom" data-id="element-6271" data-mp="2kmkfo8r00041">
                <ColumnHead slug="claude" name="Claude" data-id="element-6272" data-mp="2pldty3e3zv59" />
              </th>
              {/* The Giga column is the answer the table exists to give, so it
               is stated as a column rather than left level with the other two.
               The orange cap lifts it into a featured column. */}
              <th scope="col" className="w-[18%] rounded-tr-[19px] border-x border-[#F3DECB] bg-[#FFF8F3] px-4 py-4 text-center align-bottom" data-id="element-6273" data-mp="21y5mpfx4ec0l">
                <ColumnHead slug="giga" name="Giga" featured data-id="element-6274" data-mp="25w3b4gj2gu2x" />
              </th>
            </tr>
          </thead>
          <tbody data-id="element-6275" data-mp="1wxhxmzsgc590">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="group border-b border-[#F1ECE3] last:border-b-0 transition-colors duration-150 ease-out hover:bg-[#FDFBF8]" data-id="element-6276" data-mp="1wx86v9zl1u29">
                {/* The description is the detail, not the point — it appears
                 over the row on hover so ten rows stay scannable, and it
                 overlays rather than expands so nothing below it moves. */}
                <th scope="row" className="relative px-5 py-4 text-[14px] font-semibold leading-snug text-[#2B211A] md:px-6" data-id="element-6277" data-mp="1r4cblzfhl9f8">
                  <span className="block" data-id="element-6278" data-mp="2etnb7dl3oxrs">{row.feature}</span>
                  <span className="pointer-events-none absolute left-4 right-2 top-[calc(100%-6px)] z-20 max-w-xl translate-y-1 rounded-xl border border-[#EFEAE1] bg-white px-3 py-2 text-[12.5px] font-normal leading-[1.45] text-[#6B5D52] opacity-0 shadow-[0_18px_40px_-20px_rgba(43,33,26,0.35)] transition-[opacity,transform] duration-150 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100 md:left-6" data-id="element-6279" data-mp="2jqwzgpwr73e1">
                    {row.description}
                  </span>
                </th>
                <td className="px-4 py-4 text-center text-[#2B211A]" data-id="element-6280" data-mp="r14if5debe4u">
                  <ComparisonValue value={row.chatgpt} data-id="element-6281" data-mp="m448ukhc7t8z" />
                </td>
                <td className="px-4 py-4 text-center text-[#2B211A]" data-id="element-6282" data-mp="zupx6opk8ve9">
                  <ComparisonValue value={row.anthropic} data-id="element-6283" data-mp="vtnshscvjr4w" />
                </td>
                <td className="border-x border-[#F3DECB] bg-[#FFF8F3] px-4 py-4 text-center text-[#2B211A] group-last:rounded-br-[19px] group-last:border-b-0" data-id="element-6284" data-mp="84ldks9mkce3">
                  <ComparisonValue value={row.giga} highlight data-id="element-6285" data-mp="37314rweff1c" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
/** Each column is named by its product, so the mark belongs with the name. */
function ColumnHead({
  slug,
  name,
  featured = false
}: {
  slug: string;
  name: string;
  featured?: boolean;
}) {
  // Resolved through BrandLogo so a mark that disappears from the icon CDN
  // degrades to the brand's favicon instead of leaving a broken image — and so
  // ChatGPT's knot picks up the same optical scaling it gets everywhere else.
  return <span className={`inline-flex items-center gap-2 rounded-full border py-1.5 pl-2.5 pr-3.5 ${featured ? 'border-[#EA580C]/30 bg-white' : 'border-[#E7E0D4] bg-[#FCFAF6]'}`} data-id="element-6286" data-mp="q5h2yhl6y1yh">
      <BrandLogo slug={slug} className="size-[16px] shrink-0" data-id="element-6287" data-mp="z7yn8qhackiu" />
      <span className={`font-title text-[13.5px] font-bold leading-none md:text-[14.5px] ${featured ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-6288" data-mp="2hg11riipjs1">
        {name}
      </span>
    </span>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  // A bare glyph reads as a wireframe placeholder. Giving support and absence
  // their own tokens makes the column scannable as a shape, not a symbol.
  // One colour for "supported", in every column: a tick has to mean the same
  // thing wherever it appears, so the Giga column is featured by its cream
  // fill and orange borders rather than by recolouring the semantics. Green is
  // the same success token used in the run traces and pulse health strips.
  if (value === true) {
    return <span className="inline-flex size-[22px] items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-600/15" aria-label="Supported" data-id="element-6289" data-mp="32g8tj0f29flb">
        <Check size={13} strokeWidth={3.2} aria-hidden="true" data-id="element-6290" data-mp="2yedu61goxca6" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex size-[22px] items-center justify-center rounded-full border border-[#EFEAE1] text-[#CDC3B4]" aria-label="Not supported" data-id="element-6291" data-mp="2thng1jog4qre">
        <X size={12} strokeWidth={2.8} aria-hidden="true" data-id="element-6292" data-mp="2jqss8avjxj3u" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="inline-flex size-[22px] items-center justify-center text-[15px] leading-none text-[#D8CFC2]" data-id="element-6293" data-mp="2erzcscwpfi11">
        –
      </span>;
  }
  return <span className={`inline-block rounded-md border px-1.5 py-1 text-[11px] font-semibold leading-tight ${highlight ? 'border-[#EA580C]/25 bg-white text-[#EA580C]' : 'border-[#EFEAE1] bg-[#FCFAF6] text-[#6B5D52]'}`} data-id="element-6294" data-mp="25pgdu3w1hpk2">
      {value}
    </span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-6295" data-mp="3w264ylv9hy85">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-6296" data-mp="4vlfv7orp0dr">
        <Icon size={22} strokeWidth={2.4} data-id="element-6297" data-mp="3m4i7ho69ucma" />
      </span>
      {label}
    </p>;
}